<script setup lang="ts">
  import type { ServiceMenuItem } from '@/models/service';
  import type { PropType } from 'vue';
  import { useActiveElement } from '@vueuse/core';

  const props = defineProps({
    items: {
      type: Array as PropType<ServiceMenuItem[]>,
      required: true
    }
  });

  const localePath = useLocalePath();

  const { t } = useI18n();

  const selectedSlug = computed(() => useRoute().params.slug ?? null);

  const selectedTitle = computed(() =>
    selectedSlug.value ? props.items?.find((item) => item.slug === selectedSlug.value)?.title : t('menu.selectUseCase')
  );

  const menuRef = ref<HTMLDivElement | null>(null);
  const isOpen = ref(false);

  const showMenu = () => {
    isOpen.value = true;
  };

  const hideMenu = () => {
    isOpen.value = false;
  };

  const toggleMenu = () => {
    if (isOpen.value) hideMenu();
    else showMenu();
  };

  const hideMenuOnBlur = () => {
    const activeElement = useActiveElement();

    if (
      !activeElement.value ||
      activeElement.value.classList.contains('use-case-menu-mobile') ||
      activeElement.value.classList.contains('use-case-menu-mobile__item')
    ) {
      return;
    }

    hideMenu();
  };
</script>

<template>
  <div
    ref="menuRef"
    :class="['use-case-menu-mobile', { 'use-case-menu-mobile--selected': !!selectedSlug, 'use-case-menu-mobile--open': isOpen }]"
    tabindex="0"
    @click="toggleMenu"
    @blur="hideMenuOnBlur">
    <div class="use-case-menu-mobile__dropdown">
      <div class="use-case-menu-mobile__list">
        <NuxtLink
          v-for="(item, index) in items"
          :key="index"
          tabindex="0"
          :class="['use-case-menu-mobile__item', { 'use-case-menu-mobile__item--selected': item.slug === selectedSlug }]"
          :to="localePath({ name: 'use-cases-slug', params: { slug: item.slug } })"
          @click.prevent.stop="hideMenu"
          @focus="showMenu"
          @blur="hideMenuOnBlur">
          <span>{{ item.title }}</span>
        </NuxtLink>
      </div>
    </div>
    <div class="use-case-menu-mobile__display">
      <span class="use-case-menu-mobile__text">{{ selectedTitle }}</span>
      <span class="use-case-menu-mobile__chevron" />
    </div>
  </div>
</template>
